import lowerFirst from 'lodash/lowerFirst';
import upperFirst from 'lodash/upperFirst';
import {Component} from 'panel';

import {ordinalizeNumber} from 'common/util';
import {getCurrentMoment} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {getRetentionBucketName, getRetentionBucketStartAndEnd} from 'common/report/retention/util';
import {RetentionQuery} from 'common/report/retention/models/retention-query';
import {BirthIntervalUnit, RetentionType} from 'common/report/retention/util/types';
import {NON_INTERVALIZED_VALUE} from 'common/report/retention/util/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isCohortSegment, isDateSegment, isNumericSegment} from 'common/query-builder/query-block/segment-menu/types';
import {formatDate} from 'common/report/retention/util/parsing';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {ButtonOption} from 'common/components/popover-card/types';
import {CohortType} from 'common/report/retention/models/types';
import {emitTrackEvent} from 'common/util/dom';
import {RetentionTrackingEvents as Events} from 'common/report/retention/util/constants';
import {openUserSidebar} from 'common/widgets/user-sidebar';
import {ChartType, ReportName} from 'common/report/constants';
import {ConstantPropertyMapper} from 'common/report/util/properties';

import {createCohort} from './util';

export interface TableAttrs {
  bornEventName: string;
  bucketSizes: number[];
  dataGroupName: string;
  dataGroupNamePlural: string;
  eventName: string;
  groupBy: RetentionQuery['groupBy'];
  unbounded: boolean;
  dateUnit: BirthIntervalUnit;
  dateRange: BookmarkDateRange;
  retentionUnit: TimeUnit;
  retentionType: RetentionType;
  projectTimezone: string;
  query: RetentionQuery;
}

export interface Cell {
  absoluteValue: string;
  absoluteDropOffValue: string;
  cohortSize: number;
  columnIdx: number;
  rowIdx: number;
  groupedRowIdx?: number;
  completeDate: string | false;
  enteredValue: string;
  enteredDate?: Date;
  incomplete: boolean;
  isAggregated: boolean;
}

export enum ViewUsersType {
  Retain = `view-users-retain`,
  DropOff = `view-users-drop-off`,
}

export class RetentionPopover {
  private tableAttrs: TableAttrs;
  private cell: Cell;

  constructor(tableAttrs: TableAttrs, cell: Cell) {
    this.tableAttrs = tableAttrs;
    this.cell = cell;
  }

  public isAggregatedCell() {
    return this.cell.isAggregated;
  }

  /**
   * Calculate position so that popover doesn't overflow bodyRect right or bottom.
   *
   * Assumes that the tableRect is fully visible.
   */
  public getTablePopoverPosition(elRect: DOMRect, tableRect: DOMRect, bodyRect: DOMRect) {
    const estWidth = 300;
    const estHeight = 350;
    const xSpacing = 10;

    let x = -tableRect.x,
      y = -tableRect.y;

    if (elRect.x + elRect.width + estWidth + xSpacing > bodyRect.width) {
      x += elRect.x - estWidth;
    } else {
      x += elRect.x + elRect.width + xSpacing;
    }

    if (elRect.y + estHeight > bodyRect.bottom) {
      y += bodyRect.bottom - estHeight;
    } else {
      y += elRect.y;
    }

    return {x, y};
  }

  public getTitle() {
    let title = ``;
    if (this.tableAttrs.retentionType === `addiction`) {
      title = `Frequency: ${this.cell.columnIdx + 2} ${this.tableAttrs.dateUnit === `day` ? `hours` : `days`}`;
    } else {
      title = getRetentionBucketName({
        bucketIndex: this.cell.columnIdx,
        bookmarkParams: this.tableAttrs.query.generateBookmark(),
      });
    }

    return `${title} / ${this.getEnteredValue()}`;
  }

  public getSubtitle() {
    const usersStr = this.tableAttrs.dataGroupNamePlural;

    if (this.isAggregatedCell()) {
      if (this.cell.completeDate) {
        return `Average of ${usersStr} who retained during any interval.`;
      } else {
        return `Average of ${usersStr} who retained during a complete interval.`;
      }
    } else {
      return `For ${usersStr} who entered ${this.isSegmented() ? `with` : `on`} ${this.cell.enteredValue}`;
    }
  }

  public isSegmented() {
    return this.tableAttrs.groupBy.length > 0;
  }

  public getRetainedCount() {
    return this.cell.absoluteValue;
  }

  public getDataGroupName() {
    return this.tableAttrs.dataGroupName;
  }

  public getDataGroupNamePlural() {
    return this.tableAttrs.dataGroupNamePlural;
  }

  public getDropOffCount() {
    return this.cell.absoluteDropOffValue;
  }

  public getEnteredValue() {
    return this.cell.enteredValue;
  }

  public getCohortDisableMessage() {
    let tooltip = ``;
    if (this.tableAttrs.groupBy.some(isCohortSegment)) {
      tooltip = `To create a cohort please select all ${this.tableAttrs.dataGroupNamePlural} in the query`;
    } else if (this.tableAttrs.groupBy.some(isNumericSegment)) {
      tooltip = `Creating cohorts is disabled when breaking down by a numeric property`;
    } else if (this.cell.absoluteValue === `0` && this.cell.absoluteDropOffValue === `0`) {
      tooltip = `Cohorts can be created when there is at least 1 eligible ${this.tableAttrs.dataGroupName}`;
    } else if (this.tableAttrs.groupBy.some(isDateSegment)) {
      tooltip = `Creating cohorts is disabled when breaking down by a date property`;
    }
    return tooltip;
  }

  public getSegmentLabel(type: CohortType) {
    const action = type === CohortType.SpecificIntervalRetained ? `retained` : `dropped off`;
    if (this.cell.isAggregated) {
      if (this.cell.completeDate) {
        return `who ${action} during any interval.`;
      } else {
        return `who ${action} during a complete interval.`;
      }
    }
    if (this.tableAttrs.query.chartType === ChartType.RetentionTrend) {
      return `${action} who entered on ${this.tableAttrs.dateUnit} of ${formatDate(
        this.cell.enteredDate,
        this.tableAttrs.projectTimezone,
      )}`;
    }
    return `who ${action} on ${formatDate(this.cell.enteredDate, this.tableAttrs.projectTimezone)}`;
  }

  public getSegments() {
    if (this.isSegmented()) {
      const enteredValue = this.getEnteredValue();
      // convert the segment value to its original value
      const segments = enteredValue.split(` / `).map((segment: string) => {
        return ConstantPropertyMapper[segment] || segment;
      });

      return segments;
    }
    return [];
  }

  private getViewUserDisableMessage(type: CohortType) {
    let tooltip = ``;
    if (this.tableAttrs.groupBy.some(isCohortSegment)) {
      tooltip = `View ${this.tableAttrs.dataGroupNamePlural} is disabled when breaking down by cohort`;
    } else if (this.tableAttrs.groupBy.some(isNumericSegment)) {
      tooltip = `View ${this.tableAttrs.dataGroupNamePlural} is disabled when breaking down by a numeric property`;
    } else if (
      (this.cell.absoluteValue === `0` && type === CohortType.SpecificIntervalRetained) ||
      (this.cell.absoluteDropOffValue === `0` && type === CohortType.SpecificIntervalDropOff)
    ) {
      tooltip = `Can only view ${this.tableAttrs.dataGroupNamePlural} when there is at least 1 eligible ${this.tableAttrs.dataGroupName}`;
    }
    return tooltip;
  }

  public getButtonOptions(): ButtonOption[] {
    const buttonOptions = [];
    buttonOptions.push({
      label: `View retained ${this.tableAttrs.dataGroupNamePlural}`,
      value: ViewUsersType.Retain,
      isDisabled: !!this.getViewUserDisableMessage(CohortType.SpecificIntervalRetained),
      tooltip: this.getViewUserDisableMessage(CohortType.SpecificIntervalRetained),
    });
    buttonOptions.push({
      label: `View dropped off ${this.tableAttrs.dataGroupNamePlural}`,
      value: ViewUsersType.DropOff,
      isDisabled: !!this.getViewUserDisableMessage(CohortType.SpecificIntervalDropOff),
      tooltip: this.getViewUserDisableMessage(CohortType.SpecificIntervalDropOff),
    });
    return buttonOptions;
  }

  public getCohortDetails(): CohortDetail[] {
    const warning = this.cell.incomplete
      ? `${upperFirst(this.tableAttrs.dataGroupNamePlural)} will continue to qualify until the end of ${
          this.cell.completeDate
        }`
      : ``;

    let reportType = `retention`;
    if (this.tableAttrs.retentionType === `addiction`) {
      const frequencyCriteria = this.tableAttrs.query.cumulative ? `cumulative` : `non-cumulative`;
      reportType = `addiction-${frequencyCriteria}`;
    }

    return [CohortType.SpecificIntervalRetained, CohortType.SpecificIntervalDropOff].map((type) => ({
      warning,
      description: this.getCohortDescription(type),
      label: this.getLabelForCohortDetail(type),
      type,
      cohort: createCohort({
        name: this.getCohortName(type),
        description: this.getCohortDescription(type),
        bookmark: this.tableAttrs.query.generateBookmark(),
        behaviorType: type,
        columnIdx: this.cell.columnIdx,
        segments: this.getSegments(),
        groupedRowIdx: this.cell.groupedRowIdx,
        isSegmented: this.isSegmented(),
        reportType,
      }),
    }));
  }

  private getLabelForCohortDetail(type: CohortType) {
    if (type === CohortType.SpecificIntervalRetained) {
      return this.isAggregatedCell()
        ? `All retained ${this.tableAttrs.dataGroupNamePlural} that contribute to average`
        : `Retained ${this.tableAttrs.dataGroupNamePlural}`;
    }
    return this.isAggregatedCell()
      ? `All dropped ${this.tableAttrs.dataGroupNamePlural} that contribute to average`
      : `Dropped ${this.tableAttrs.dataGroupNamePlural}`;
  }

  private getCohortName(behaviorType: CohortType) {
    if (this.tableAttrs.retentionType === `addiction`) {
      const dateUnit = this.tableAttrs.dateUnit === `day` ? `hours` : `days`;
      const userCohortType = behaviorType === CohortType.SpecificIntervalDropOff ? `Dropped Users` : `Retained Users`;
      return `[Frequency] ${this.cell.columnIdx + 2} ${dateUnit} ${userCohortType}`;
    }

    const bucketName = getRetentionBucketName({
      bucketIndex: this.cell.columnIdx,
      bookmarkParams: this.tableAttrs.query.generateBookmark(),
    });
    const userCohortType = behaviorType === CohortType.SpecificIntervalDropOff ? `Dropped Users` : `Retained Users`;
    return `[Retention] ${bucketName} ${userCohortType}`;
  }

  private getCustomBucketTitle(unbounded: boolean) {
    const customBucketRange = getRetentionBucketStartAndEnd({
      bucketIndex: this.cell.columnIdx,
      bookmarkParams: this.tableAttrs.query.generateBookmark(),
    });

    return `${unbounded ? `≥` : ``}${
      customBucketRange.start === customBucketRange.end || unbounded
        ? `${customBucketRange.start}`
        : `${customBucketRange.start}-${customBucketRange.end}`
    } ${pluralize(this.tableAttrs.retentionUnit, customBucketRange.end)} later`;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public getCohortDescription(behaviorType: CohortType) {
    if (this.tableAttrs.retentionType === `addiction`) {
      // To do: Return a fully formatted string for addiction once we add Create
      // Cohort functionality for the Frequency Report.

      // Description example:
      // Users who did Event A at least 2 hours in a day in the last 3 months.

      const groupName = upperFirst(this.tableAttrs.dataGroupNamePlural);
      return `${groupName} who did "${this.tableAttrs.eventName}" at least `;
    }

    const dateUnit = this.tableAttrs.dateUnit;
    const groupName = upperFirst(this.tableAttrs.dataGroupNamePlural);

    let retentionLabel: string;
    const {retentionUnit} = this.tableAttrs;
    if (this.tableAttrs.unbounded) {
      if (this.cell.columnIdx === 0) {
        retentionLabel = `any time later`;
      } else {
        retentionLabel = this.tableAttrs.bucketSizes.length
          ? this.getCustomBucketTitle(true)
          : `≥${this.cell.columnIdx} ${pluralize(retentionUnit, this.cell.columnIdx)} later`;
      }
    } else {
      if (this.cell.columnIdx === 0) {
        retentionLabel = `within 1 ${retentionUnit}`;
      } else {
        retentionLabel = this.tableAttrs.bucketSizes.length
          ? this.getCustomBucketTitle(false)
          : `${this.cell.columnIdx}-${this.cell.columnIdx + 1} ${retentionUnit}s later`;
      }
    }

    const dateRange = DateRange.fromBookmarkFormat(this.tableAttrs.dateRange);
    const dateRangeLabel = lowerFirst(
      getDateRangeLabel(dateRange, null, null, getCurrentMoment({localized: true}), `sentence`),
    );
    let birthIntervalLabel: string;
    if (dateUnit === NON_INTERVALIZED_VALUE) {
      birthIntervalLabel = dateRangeLabel;
    } else if (!Number.isFinite(this.cell.groupedRowIdx)) {
      // average row
      birthIntervalLabel = `during any ${dateUnit} ${dateRangeLabel}`;
    } else if (dateRange.isRelativeWindow() || dateRange.isRolling()) {
      const birthIntervalIdx = this.cell.groupedRowIdx;
      birthIntervalLabel = `on the ${ordinalizeNumber(birthIntervalIdx + 1)} ${dateUnit} ${dateRangeLabel}`;
    } else {
      const formattedEnteredDate = formatDate(this.cell.enteredDate, this.tableAttrs.projectTimezone);
      if (dateUnit === TimeUnit.Day) {
        birthIntervalLabel = `on ${formattedEnteredDate}`;
      } else {
        birthIntervalLabel = `during the ${dateUnit} of ${formattedEnteredDate}`;
      }
    }

    return [
      `${groupName} who did`,
      `"${this.tableAttrs.bornEventName}"`,
      ...(this.isSegmented() ? [`with ${this.cell.enteredValue}`] : []),
      birthIntervalLabel,
      behaviorType === CohortType.SpecificIntervalDropOff ? `and then did not do` : `and then did`,
      `"${this.tableAttrs.eventName}"`,
      retentionLabel,
    ].join(` `);
  }

  public hideCohortFunctionality() {
    // We don't have QueryAPI support for cohort create in addiction yet.
    return this.tableAttrs.retentionType === `addiction`;
  }
}

export function trackPopover({
  eventName,
  properties,
  component,
  query,
  popover,
}: {
  eventName: string;
  properties?: Record<string, any>;
  component: Component<any, any, any, any, any>;
  query: RetentionQuery;
  popover: RetentionPopover;
}) {
  const popoverClickedElement = `${popover.getTitle()} since ${popover.getEnteredValue()}`;
  emitTrackEvent(component, {
    eventName,
    props: {
      'event name': query.event,
      tab: `retention`,
      'clicked element': popoverClickedElement,
      'element type': `table cell`,
      ...(properties ?? {}),
    },
  });
}

export function handlePopoverAction({
  action,
  color,
  component,
  query,
  popover,
}: {
  action: ViewUsersType;
  color: string;
  component: Component<any, any, any, any, any>;
  query: RetentionQuery;
  popover: RetentionPopover;
}): void {
  if ([ViewUsersType.Retain, ViewUsersType.DropOff].includes(action)) {
    const cohortDetails = popover.getCohortDetails();
    const type =
      action === ViewUsersType.Retain ? CohortType.SpecificIntervalRetained : CohortType.SpecificIntervalDropOff;
    const cohortDetail = cohortDetails.find((c) => c.type === type);
    trackPopover({
      eventName: Events.PopoverViewUsers,
      component,
      query,
      popover,
    });
    const segments = popover.isSegmented() ? [popover.getEnteredValue()] : [];
    openUserSidebar(
      {
        'hide-cohort-functionality': popover.hideCohortFunctionality(),
        'include-all-users': true,
        'report-name': ReportName.Retention,
        segments: [popover.getTitle(), ...segments],
        'data-group-name': query.getDataGroupName({plural: false}),
        'segment-label': popover.getSegmentLabel(type),
        color,
      },
      {
        cohort: cohortDetail.cohort,
        cohortDetails: [cohortDetail],
      },
    );
  } else {
    throw new Error(`unsupported action`);
  }
}
